import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isProduction } from '@/config/config'
import {
  ORG_STATUSES,
  useQAServices,
  useRunOnboarding,
  useSeedActiveFleet,
  useUpdateDotNumber,
  useUpdateOrgStatus,
} from '../../../../models/qa'
import { GeicoQaCard } from './GeicoQaCard'
import { ImportCustomerCard } from './ImportCustomerCard'

export function QA() {
  const [targetService, setTargetService] = useState('')
  const [dotNumber, setDotNumber] = useState('')
  const [orgStatus, setOrgStatus] = useState('')
  const [seedEmail, setSeedEmail] = useState('')
  const [seedTrucks, setSeedTrucks] = useState(5)
  const [seedTrailers, setSeedTrailers] = useState(2)
  const [seedDrivers, setSeedDrivers] = useState(5)
  const { data: services, isLoading: isLoadingServices } = useQAServices()
  const { mutate: runOnboarding, isPending, data: result, error } = useRunOnboarding()
  const {
    mutate: updateDotNumber,
    isPending: isDotPending,
    data: dotResult,
    error: dotError,
  } = useUpdateDotNumber()
  const {
    mutate: updateOrgStatus,
    isPending: isStatusPending,
    data: statusResult,
    error: statusError,
  } = useUpdateOrgStatus()
  const {
    mutate: seedActiveFleet,
    isPending: isSeedPending,
    data: seedResult,
    error: seedError,
  } = useSeedActiveFleet()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (targetService) {
      runOnboarding(targetService)
    }
  }

  function handleDotNumberSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (dotNumber) {
      updateDotNumber(dotNumber)
    }
  }

  function handleOrgStatusSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (orgStatus) {
      updateOrgStatus(orgStatus)
    }
  }

  function handleSeedSubmit(e: React.FormEvent) {
    e.preventDefault()
    seedActiveFleet({
      email: seedEmail,
      trucks: seedTrucks,
      trailers: seedTrailers,
      drivers: seedDrivers,
    })
  }

  return (
    <>
      <Helmet>
        <title>QA</title>
      </Helmet>
      <div className="grid w-full grid-cols-3 gap-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>QA Onboarding</CardTitle>
            <CardDescription>
              Run the onboarding flow to advance an application to a specific service step.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="target-service">Target service</Label>
                <Select value={targetService} onValueChange={setTargetService} disabled={isLoadingServices}>
                  <SelectTrigger id="target-service" className="w-full">
                    <SelectValue placeholder="Select a target service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services?.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={!targetService || isPending}
                loading={isPending ? 'Running...' : undefined}
              >
                Run onboarding
              </Button>
              {result && (
                <p className="text-sm text-green-600">
                  Quote advanced to <strong>{result.nextService}</strong>.{' '}
                  <Link to={`/application`} className="underline">
                    Open application
                  </Link>
                </p>
              )}
              {error && <p className="text-sm text-red-600">Error: {(error as Error).message}</p>}
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Change DOT Number</CardTitle>
            <CardDescription>Update the DOT number for the current organization.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDotNumberSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="dot-number">DOT number</Label>
                <Input
                  id="dot-number"
                  value={dotNumber}
                  onChange={(e) => setDotNumber(e.target.value)}
                  placeholder="Enter DOT number"
                />
              </div>
              <Button type="submit" disabled={!dotNumber || isDotPending}>
                {isDotPending ? 'Updating...' : 'Update DOT number'}
              </Button>
              {dotResult && (
                <p className="text-sm text-green-600">
                  DOT number updated to <strong>{dotResult.dotNumber}</strong>.
                </p>
              )}
              {dotError && <p className="text-sm text-red-600">Error: {(dotError as Error).message}</p>}
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Change Org Status</CardTitle>
            <CardDescription>Update the status of the current organization.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleOrgStatusSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="org-status">Organization status</Label>
                <Select value={orgStatus} onValueChange={setOrgStatus}>
                  <SelectTrigger id="org-status" className="w-full">
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={!orgStatus || isStatusPending}>
                {isStatusPending ? 'Updating...' : 'Update status'}
              </Button>
              {statusResult && (
                <p className="text-sm text-green-600">
                  Org status updated to <strong>{statusResult.status}</strong>.
                </p>
              )}
              {statusError && <p className="text-sm text-red-600">Error: {(statusError as Error).message}</p>}
            </form>
          </CardContent>
        </Card>

        {/* Every endpoint behind this 404s in production, so don't offer the door. */}
        {!isProduction && (
          <Card>
            <CardHeader>
              <CardTitle>QA Emails</CardTitle>
              <CardDescription>
                Send email to the agent as a fake customer and read its replies. Nothing is delivered.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/insure/internal/qa/emails">Open QA inbox</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Seed Active Fleet</CardTitle>
            <CardDescription>
              Seed a fleet with an active policy. Leave email empty to create a fresh fleet, or enter an email
              to seed under that user.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSeedSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="seed-email">Email (optional)</Label>
                <Input
                  id="seed-email"
                  type="email"
                  value={seedEmail}
                  onChange={(e) => setSeedEmail(e.target.value)}
                  placeholder="Leave empty for a new fleet"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="seed-trucks">Trucks</Label>
                  <Input
                    id="seed-trucks"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={seedTrucks}
                    onChange={(e) => setSeedTrucks(Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="seed-trailers">Trailers</Label>
                  <Input
                    id="seed-trailers"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={seedTrailers}
                    onChange={(e) => setSeedTrailers(Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="seed-drivers">Drivers</Label>
                  <Input
                    id="seed-drivers"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={seedDrivers}
                    onChange={(e) => setSeedDrivers(Number(e.target.value))}
                  />
                </div>
              </div>
              <Button type="submit" disabled={isSeedPending}>
                {isSeedPending ? 'Seeding...' : 'Seed active fleet'}
              </Button>
              {seedResult && (
                <p className="text-sm text-green-600">
                  Seeded {seedResult.reusedOrg ? 'existing' : 'new'} org <strong>{seedResult.orgName}</strong>{' '}
                  for {seedResult.email} ({seedResult.trucks} trucks, {seedResult.trailers} trailers,{' '}
                  {seedResult.drivers} drivers).
                </p>
              )}
              {seedError && <p className="text-sm text-red-600">Error: {(seedError as Error).message}</p>}
            </form>
          </CardContent>
        </Card>

        {/* Writes fixture rows verbatim, so the endpoint refuses to run in production. */}
        {!isProduction && <ImportCustomerCard />}

        {/* Spends a real GEICO portal session, so the endpoints 404 in production. */}
        {!isProduction && <GeicoQaCard />}
      </div>
    </>
  )
}
